import type {
  ConnectedSourceId,
  Outlet,
  RecoveryData,
} from '../types';

function nz(v: number | null | undefined, fallback = 0): number {
  return v == null ? fallback : v;
}

function eurOf(outlet: Outlet, cov: number): number {
  return Math.round(cov * nz(outlet.spendCover, 0));
}

/**
 * Rebuild Recovery Radar tallies for one outlet so Live stays single-restaurant.
 * Keeps estate baseline capture for comparison; filters opportunities to the outlet.
 */
export function recoveryForOutlet(
  base: RecoveryData | null,
  outlet: Outlet | null | undefined,
  connected: ConnectedSourceId[],
): RecoveryData | null {
  if (!base) return null;
  if (!outlet) return base;

  const hasPos = connected.includes('pos') || base.hasPos;
  const recoveredCov = nz(outlet.recoveredGapCovers);
  const recoverableCov = nz(outlet.recoverableGapCovers);
  const lostCov = nz(outlet.lostGapCovers);
  const gapCov = nz(outlet.gapCovers);
  const recoveredCount = nz(outlet.recoveredGapCount);
  const recoverableCount = nz(outlet.recoverableGapCount);
  const lostCount = nz(outlet.lostGapCount);
  const gapCount = nz(outlet.gapCount);
  const walkInCov = nz(outlet.walkInCovers);
  const walkInCount = nz(outlet.walkInCount);

  const recoveredEur = eurOf(outlet, recoveredCov);
  const recoverableEur = eurOf(outlet, recoverableCov);
  const lostEur = eurOf(outlet, lostCov);
  const gapEur = eurOf(outlet, gapCov);
  const walkInEur = eurOf(outlet, walkInCov);

  const opportunities = base.opportunities.filter(o => o.outletId === outlet.id);
  const onTableCov = opportunities.reduce((s, o) => s + o.cov, 0);
  const onTableEur = opportunities.reduce((s, o) => s + o.eur, 0);

  const resolved = hasPos ? recoveredEur + lostEur : recoveredCov + lostCov;
  const won = hasPos ? recoveredEur : recoveredCov;
  const captureRate =
    resolved > 0 ? Math.round((won / resolved) * 100) : null;

  return {
    ...base,
    hasPos,
    opportunities,
    onTableCov,
    onTableEur,
    onTableCount: opportunities.length,
    recoveredCov,
    recoveredEur,
    recoveredCount,
    recoverableCov,
    recoverableEur,
    recoverableCount,
    lostCov,
    lostEur,
    lostCount,
    gapCov,
    gapEur,
    gapCount,
    gapUnknownCount: nz(outlet.gapUnknownCount),
    captureRate,
    walkInCov,
    walkInEur,
    walkInCount,
    byRegion: [],
    patterns: [],
  };
}
